import type { EditTarget } from 'config/analysisConfig';
import type { SerializedSegmentManagerDraft } from '../segmentManager/core/SegmentManagerCore';

const DB_NAME = 'ctffr-edit-drafts';
const STORE_NAME = 'drafts';
const DB_VERSION = 1;
const LOCAL_KEY_PREFIX = 'CTFFR_EDIT_DRAFT_V2::';
const LOCAL_KEY_LIST = 'CTFFR_EDIT_DRAFT_KEYS_V2';
const ELECTRON_STORE_KEY = 'ctffrEditDrafts';

export type CTFFREditDraft = SerializedSegmentManagerDraft & {
  key: string;
  savedAt: number;
  seriesId: string;
  editMode: EditTarget;
  renderingQuality: string;
  uiState?: {
    regulatorConnectThickness?: number;
    regulatorConnectHuEnabled?: boolean;
    regulatorConnectHuMin?: number;
    regulatorConnectHuMax?: number;
    cprModificationTool?: string;
    cprPixelBrushMode?: string;
    cprPixelBrushSize?: number;
    cprSculptSize?: number;
  };
};

type DraftSearchParams = {
  key?: string | null;
  seriesId?: string | null;
  editMode?: EditTarget;
  renderingQuality?: string;
};

function getDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.indexedDB) {
      reject(new Error('IndexedDB is not available.'));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open edit draft database.'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };
  });
}

function runStoreRequest<T>(
  mode: IDBTransactionMode,
  createRequest: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return getDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        const request = createRequest(store);

        request.onsuccess = () => {
          resolve(request.result);
        };

        request.onerror = () => {
          reject(request.error ?? new Error('Edit draft request failed.'));
        };

        tx.oncomplete = () => {
          db.close();
        };

        tx.onerror = () => {
          reject(tx.error ?? new Error('Edit draft transaction failed.'));
          db.close();
        };
      }),
  );
}

export function buildCTFFREditDraftKey({
  seriesId,
  editMode,
  renderingQuality,
}: {
  seriesId?: string | null;
  editMode?: EditTarget;
  renderingQuality: string;
}) {
  if (!seriesId || !editMode) return null;
  return `CTFFR_EDIT_DRAFT::${seriesId}::${editMode}::${renderingQuality}`;
}

function localStorageAvailable() {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch {
    return false;
  }
}

function localDraftKey(key: string) {
  return `${LOCAL_KEY_PREFIX}${key}`;
}

function readLocalKeyList() {
  if (!localStorageAvailable()) return [] as string[];
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY_LIST);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeLocalKeyList(keys: string[]) {
  if (!localStorageAvailable()) return;
  try {
    window.localStorage.setItem(LOCAL_KEY_LIST, JSON.stringify(Array.from(new Set(keys))));
  } catch {
    // ignore local mirror failure; IndexedDB/electron-store remain the primary stores.
  }
}

function addLocalKey(key: string) {
  writeLocalKeyList([...readLocalKeyList(), key]);
}

function removeLocalKey(key: string) {
  writeLocalKeyList(readLocalKeyList().filter((item) => item !== key));
}

function normalizeFlippedIndices(value: unknown) {
  if (value instanceof Uint32Array) return value;
  if (Array.isArray(value)) return Uint32Array.from(value.map(Number));
  if (value && typeof value === 'object') {
    return Uint32Array.from(
      Object.keys(value as Record<string, unknown>)
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => Number((value as Record<string, unknown>)[key])),
    );
  }
  return new Uint32Array();
}

function serializeDelta(delta: any) {
  return {
    ...delta,
    flippedIndices: Array.from(normalizeFlippedIndices(delta?.flippedIndices)),
  };
}

function deserializeDelta(delta: any) {
  return {
    ...delta,
    flippedIndices: normalizeFlippedIndices(delta?.flippedIndices),
  };
}

function serializeDeltaList(deltas: any[] | undefined) {
  return (Array.isArray(deltas) ? deltas : []).map(serializeDelta);
}

function deserializeDeltaList(deltas: any[] | undefined) {
  return (Array.isArray(deltas) ? deltas : []).map(deserializeDelta);
}

function serializeDraftForJson(draft: CTFFREditDraft) {
  return {
    ...draft,
    undoStack: serializeDeltaList(draft.undoStack),
    redoStack: serializeDeltaList(draft.redoStack),
    externalDeltas: (draft.externalDeltas ?? []).map(([sourceId, deltas]) => [
      sourceId,
      serializeDeltaList(deltas as any[]),
    ]),
    externalBaselines: (draft.externalBaselines ?? []).map(([sourceId, deltas]) => [
      sourceId,
      serializeDeltaList(deltas as any[]),
    ]),
  };
}

function deserializeDraftFromJson(raw: unknown): CTFFREditDraft | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const value = raw as any;
  if (!value.key || !value.seriesId || !value.editMode || value.version !== 1) return undefined;

  return {
    ...value,
    undoStack: deserializeDeltaList(value.undoStack),
    redoStack: deserializeDeltaList(value.redoStack),
    externalDeltas: (Array.isArray(value.externalDeltas) ? value.externalDeltas : []).map(
      ([sourceId, deltas]: [string, any[]]) => [sourceId, deserializeDeltaList(deltas)],
    ),
    externalBaselines: (Array.isArray(value.externalBaselines) ? value.externalBaselines : []).map(
      ([sourceId, deltas]: [string, any[]]) => [sourceId, deserializeDeltaList(deltas)],
    ),
  } as CTFFREditDraft;
}

function putLocalDraft(draft: CTFFREditDraft) {
  if (!localStorageAvailable()) return false;
  try {
    window.localStorage.setItem(localDraftKey(draft.key), JSON.stringify(serializeDraftForJson(draft)));
    addLocalKey(draft.key);
    return true;
  } catch (error) {
    console.warn('[CT-FFR Edit Draft] localStorage mirror failed.', error);
    return false;
  }
}

function getLocalDraft(key: string) {
  if (!localStorageAvailable()) return undefined;
  try {
    const raw = window.localStorage.getItem(localDraftKey(key));
    return raw ? deserializeDraftFromJson(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
}

function getLocalDrafts() {
  return readLocalKeyList()
    .map(getLocalDraft)
    .filter(Boolean) as CTFFREditDraft[];
}

function deleteLocalDraft(key: string) {
  if (!localStorageAvailable()) return;
  try {
    window.localStorage.removeItem(localDraftKey(key));
    removeLocalKey(key);
  } catch {
    // ignore
  }
}

function getElectronIpcRenderer() {
  try {
    return (window as any)?.electron?.ipcRenderer ?? null;
  } catch {
    return null;
  }
}

async function loadElectronSeriesStore() {
  const ipcRenderer = getElectronIpcRenderer();
  if (!ipcRenderer?.loadSeries) return null;
  try {
    return (await ipcRenderer.loadSeries()) ?? {};
  } catch {
    return null;
  }
}

async function saveElectronSeriesStore(payload: any) {
  const ipcRenderer = getElectronIpcRenderer();
  if (!ipcRenderer?.saveSeries) return false;
  try {
    await ipcRenderer.saveSeries(payload ?? {});
    return true;
  } catch (error) {
    console.warn('[CT-FFR Edit Draft] electron-store mirror failed.', error);
    return false;
  }
}

async function putElectronDraft(draft: CTFFREditDraft) {
  const payload = await loadElectronSeriesStore();
  if (!payload) return false;
  const drafts = {
    ...(payload[ELECTRON_STORE_KEY] ?? {}),
    [draft.key]: serializeDraftForJson(draft),
  };
  return saveElectronSeriesStore({
    ...payload,
    [ELECTRON_STORE_KEY]: drafts,
  });
}

async function getElectronDraft(key: string) {
  const payload = await loadElectronSeriesStore();
  if (!payload) return undefined;
  return deserializeDraftFromJson(payload[ELECTRON_STORE_KEY]?.[key]);
}

async function getElectronDrafts() {
  const payload = await loadElectronSeriesStore();
  if (!payload) return [] as CTFFREditDraft[];
  return Object.values(payload[ELECTRON_STORE_KEY] ?? {})
    .map(deserializeDraftFromJson)
    .filter(Boolean) as CTFFREditDraft[];
}

async function deleteElectronDraft(key: string) {
  const payload = await loadElectronSeriesStore();
  if (!payload?.[ELECTRON_STORE_KEY]?.[key]) return;
  const drafts = { ...payload[ELECTRON_STORE_KEY] };
  delete drafts[key];
  await saveElectronSeriesStore({
    ...payload,
    [ELECTRON_STORE_KEY]: drafts,
  });
}

async function getIndexedDBDraft(key: string) {
  try {
    return await runStoreRequest<CTFFREditDraft | undefined>('readonly', (store) => store.get(key));
  } catch {
    return undefined;
  }
}

async function getIndexedDBDrafts() {
  try {
    return await runStoreRequest<CTFFREditDraft[]>('readonly', (store) => store.getAll());
  } catch {
    return [] as CTFFREditDraft[];
  }
}

function getBestMatchingDraft(drafts: CTFFREditDraft[], params: DraftSearchParams) {
  const matches = drafts.filter((draft) => {
    if (params.seriesId && draft.seriesId !== params.seriesId) return false;
    if (params.editMode && draft.editMode !== params.editMode) return false;
    return true;
  });
  if (matches.length === 0) return undefined;

  return matches.sort((a, b) => {
    const aExactQuality = params.renderingQuality && a.renderingQuality === params.renderingQuality ? 1 : 0;
    const bExactQuality = params.renderingQuality && b.renderingQuality === params.renderingQuality ? 1 : 0;
    if (aExactQuality !== bExactQuality) return bExactQuality - aExactQuality;
    return (b.savedAt ?? 0) - (a.savedAt ?? 0);
  })[0];
}

export async function getCTFFREditDraft(key: string) {
  return (
    (await getIndexedDBDraft(key)) ??
    getLocalDraft(key) ??
    (await getElectronDraft(key))
  );
}

export async function findCTFFREditDraft(params: DraftSearchParams) {
  if (params.key) {
    const exact = await getCTFFREditDraft(params.key);
    if (exact) return exact;
  }

  const drafts = [
    ...(await getIndexedDBDrafts()),
    ...getLocalDrafts(),
    ...(await getElectronDrafts()),
  ];
  return getBestMatchingDraft(drafts, params);
}

export async function putCTFFREditDraft(draft: CTFFREditDraft) {
  const results = await Promise.allSettled([
    runStoreRequest<IDBValidKey>('readwrite', (store) => store.put(draft)),
    Promise.resolve(putLocalDraft(draft)),
    putElectronDraft(draft),
  ]);

  const hasSuccess = results.some(
    (result) => result.status === 'fulfilled' && result.value !== false,
  );

  if (!hasSuccess) {
    throw new Error('Failed to save edit draft.');
  }
}

export async function deleteCTFFREditDraft(key: string | null | undefined) {
  if (!key) return;
  await Promise.allSettled([
    runStoreRequest<undefined>('readwrite', (store) => store.delete(key)),
    Promise.resolve(deleteLocalDraft(key)),
    deleteElectronDraft(key),
  ]);
}

export async function deleteCTFFREditDraftsFor({
  seriesId,
  editMode,
}: {
  seriesId?: string | null;
  editMode?: EditTarget;
}) {
  if (!seriesId || !editMode) return;
  const drafts = [
    ...(await getIndexedDBDrafts()),
    ...getLocalDrafts(),
    ...(await getElectronDrafts()),
  ];
  const keys = Array.from(
    new Set(
      drafts
        .filter((draft) => draft.seriesId === seriesId && draft.editMode === editMode)
        .map((draft) => draft.key),
    ),
  );
  await Promise.all(keys.map(deleteCTFFREditDraft));
}
